import { useEffect, useRef } from 'react';
import { Menu } from 'lucide-react';
import { useUIStore } from '@/stores/ui.store';
import { useAuthStore } from '@/stores/auth.store';
import { NotificationsBell } from '@/features/notifications/NotificationsBell';
import { HeaderOpenInCrm } from '@/features/properties/components/HeaderOpenInCrm';
import { HeaderSearch } from './HeaderSearch';
import { UserMenu } from './UserMenu';
import { usePageHeaderSlots } from './PageHeaderSlots';

const APP_HEADER_HEIGHT_VAR = '--app-header-height';

/**
 * The single unified app header.
 *
 * Sticky (not fixed) and in normal flow at the top of `<main>`, so its
 * height can flex with page content and the content beneath needs no
 * manual top-offset math. One bar carries everything that used to be
 * split across a thin bell-only strip and each page's own title bar:
 *
 *   • Left:  mobile hamburger + the page title/breadcrumb slot.
 *   • Right: the page's contextual actions slot + Open in CRM (property
 *            detail only) + global search + notifications bell + user menu.
 *
 * Solid `bg-card` token surface — NO backdrop-blur (design-tokens.mdc
 * §10 / fe-performance.mdc §11: blur on always-visible chrome stalls
 * integrated GPUs). Sits at `z-sticky`, below the impersonation banner
 * (`z-max`) and below floating menus (`z-dropdown`+). Sticky `top` tracks
 * `--impersonation-banner-height` so the header never slides under the banner.
 */
export function AppHeader() {
  const setSidebarMobileOpen = useUIStore((s) => s.setSidebarMobileOpen);
  const isAuthed = useAuthStore((s) => !!s.user);
  const { registerTitleEl, registerActionsEl, registerAccentEl, registerSubheaderEl } =
    usePageHeaderSlots();
  const headerRef = useRef<HTMLElement>(null);

  const handleOpenNav = () => setSidebarMobileOpen(true);

  /** Publishes the live header height so full-bleed viewers can size their canvas. */
  useEffect(() => {
    const header = headerRef.current;
    if (!header) return;

    const syncHeight = () => {
      document.documentElement.style.setProperty(
        APP_HEADER_HEIGHT_VAR,
        `${header.offsetHeight}px`,
      );
    };

    syncHeight();
    const observer = new ResizeObserver(syncHeight);
    observer.observe(header);
    return () => {
      observer.disconnect();
      document.documentElement.style.removeProperty(APP_HEADER_HEIGHT_VAR);
    };
  }, []);

  return (
    <header
      ref={headerRef}
      style={{
        // Stick below the fixed impersonation banner when present
        // (`--impersonation-banner-height` is 0px otherwise).
        top: 'var(--impersonation-banner-height)',
        paddingLeft: 'max(0px, env(safe-area-inset-left))',
        paddingRight: 'max(0px, env(safe-area-inset-right))',
      }}
      className="sticky z-sticky border-b border-border bg-card"
    >
      {/* Section stripe — 4px pinned to the header's bottom edge, coloured
          from the route by <PageHeader>. Empty (no line) on pages that
          don't render a PageHeader. */}
      <div ref={registerAccentEl} className="absolute inset-x-0 bottom-0 empty:hidden" />
      <div className="flex min-h-[var(--header-height)] flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 py-2.5 sm:flex-nowrap sm:px-6">
        {/* Left rail — hamburger (mobile) + page title slot.
            Full width below sm so search cannot cover a long title. */}
        <div className="flex w-full min-w-0 flex-1 items-center gap-2 sm:w-auto">
          <button
            type="button"
            onClick={handleOpenNav}
            aria-label="Open navigation menu"
            className="-ml-1 inline-flex h-10 w-10 shrink-0 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-accent hover:text-accent-foreground lg:hidden"
          >
            <Menu className="h-5 w-5" />
          </button>
          <div ref={registerTitleEl} className="min-w-0 flex-1" />
        </div>

        {/* Right rail — page actions, then Open in CRM glued to search, bell, and user menu. */}
        {isAuthed ? (
          <div className="flex w-full max-w-full flex-wrap items-center justify-end gap-1 sm:w-auto sm:shrink-0 sm:gap-2">
            <div
              ref={registerActionsEl}
              className="flex max-w-full flex-wrap items-center justify-end gap-1 empty:hidden sm:gap-2"
            />
            {/* CRM stays next to search when the pill row wraps. */}
            <div className="flex shrink-0 items-center gap-1 sm:gap-2">
              <HeaderOpenInCrm />
              <HeaderSearch />
              <NotificationsBell hideLabel panelAlign="right" />
              <UserMenu />
            </div>
          </div>
        ) : null}
      </div>

      {/* Optional second row — e.g. PDF viewer toolbar — scrolls with the sticky header. */}
      <div ref={registerSubheaderEl} className="empty:hidden" />
    </header>
  );
}
